import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';

export interface GenerateSeedQuestionVariantDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (seedQuestionCount: number, variantsPerSeed: number) => void;
  loading?: boolean;
}

const DEFAULT_VARIANTS_PER_SEED = 5;

export default function GenerateSeedQuestionVariantDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
}: GenerateSeedQuestionVariantDialogProps) {
  const [seedQuestionCount, setSeedQuestionCount] = useState<string>('1');
  const [variantsPerSeed, setVariantsPerSeed] = useState<string>(String(DEFAULT_VARIANTS_PER_SEED));

  const handleSubmit = useCallback(() => {
    const count = Number.parseInt(seedQuestionCount, 10);
    const variants = Number.parseInt(variantsPerSeed, 10);
    if (Number.isNaN(count) || count < 1 || Number.isNaN(variants) || variants < 1) {
      return;
    }
    onSubmit(count, variants);
  }, [seedQuestionCount, variantsPerSeed, onSubmit]);

  const handleClose = useCallback(() => {
    setSeedQuestionCount('1');
    setVariantsPerSeed(String(DEFAULT_VARIANTS_PER_SEED));
    onClose();
  }, [onClose]);

  const countNum = Number.parseInt(seedQuestionCount, 10);
  const variantsNum = Number.parseInt(variantsPerSeed, 10);
  const isValid = !Number.isNaN(countNum) && countNum >= 1 && !Number.isNaN(variantsNum) && variantsNum >= 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Generate with seed question</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{pt: 1}}>
          <Typography variant="body2" color="text.secondary">
            Process ACTIVE seed questions: generate question variants via AI and mark each seed as GENERATED. Only seed
            questions whose topic exists in this module are included.
          </Typography>

          <TextField
            label="Number of seed questions"
            type="number"
            value={seedQuestionCount}
            onChange={(e) => setSeedQuestionCount(e.target.value)}
            inputProps={{min: 1, step: 1}}
            fullWidth
            required
            helperText="How many ACTIVE seed questions to process (oldest first)"
          />

          <TextField
            label="Variants per seed"
            type="number"
            value={variantsPerSeed}
            onChange={(e) => setVariantsPerSeed(e.target.value)}
            inputProps={{min: 1, step: 1}}
            fullWidth
            required
            helperText={`Number of question variants to generate per seed (default ${DEFAULT_VARIANTS_PER_SEED})`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          sx={{
            bgcolor: '#1B263B',
            '&:hover': {bgcolor: '#1B263B', opacity: 0.8},
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
